import React, { Fragment, useState, useEffect, useRef } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import { some, isEmpty } from 'lodash-es';

import SimpleCheckbox from '../../../../../components/Checkbox/SimpleCheckbox';
import Button from '../../../../../components/Buttons/Button';
import InputText from '../../../../../components/Inputs/InputText';
import helpers from '../../../../../utils/helpers';
import { googleClientId, getUtmParams } from '../../../../../utils/ga_helpers';

const emptyObj = {};

const defaultUser = {
  firstName: '',
  lastName: '',
  email: '',
  jobFunction: '',
};

const validateField = (fieldName, fieldValue) => {
  const validationByField = {
    firstName: value => !value.trim() ? 'Please input valid First name' : '',
    lastName: value => !value.trim() ? 'Please input valid Last name' : '',
    jobFunction: value => !value.trim() ? 'Please input valid Job title' : '',
    email: value => (!value || !helpers.validateEmail(value) || value.includes('+')) ? 'Please enter a valid email address' : '',
    checked: value => !value ? 'Please accept Terms of Service' : '',
  }[fieldName];

  return validationByField ? validationByField(fieldValue) : '';
};

const agreementText = (
  <span className="label-with-links">
    By checking this box, you agree to Apptopia&apos;s <a target="_blank" rel="noopener noreferrer" href="https://apptopia.com/privacy">Privacy Notice</a>, <a target="_blank" rel="noopener noreferrer" href="https://apptopia.com/pricing">Pricing Policy</a>, <a target="_blank" rel="noopener noreferrer" href="https://apptopia.com/terms">Terms of Service</a>, <a target="_blank" rel="noopener noreferrer" href="https://apptopia.com/data-usage">Data Usage</a>, and to receive the occasional product and marketing email from us.
  </span>
);

let googleId;
googleClientId().then((id) => {
  googleId = id;
});

const UserReportForm = ({
  sitekey,
  title,
  isProcessing,
  customErrors = emptyObj,
  submitButtonText,
  skipAgreementField = false,
  enableTopBackButton = false,
  enableBottomBackButton = false,
  className,

  setProcessing,
  onStepBack,
  onSubmit,
}) => {
  const recaptchaRef = useRef();

  const [user, setUser] = useState(defaultUser);
  const [checked, setChecked] = useState(skipAgreementField);
  const [errors, setErrors] = useState({ ...defaultUser, checked: '' });

  const isFormInvalid = some(errors, value => !!value);
  const hasUnfilled = some(user, isEmpty) || !checked;

  const submitDisabled = isProcessing || hasUnfilled || isFormInvalid;

  useEffect(() => {
    setErrors(prevErrors => ({ ...prevErrors, ...customErrors }));
  }, [customErrors]);

  const handleFieldChange = (value, { target: { name } }) => {
    setUser(prevState => ({ ...prevState, [name]: value }));
    setErrors(prevState => ({ ...prevState, [name]: validateField(name, value) }));
  };

  const handleAcceptAgreement = (value) => {
    setChecked(value);
    setErrors(prevState => ({ ...prevState, checked: validateField('checked', value) }));
  };

  const handleGoBack = () => {
    if (onStepBack) onStepBack();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitDisabled) return;

    setProcessing(true);
    const userData = {
      first_name: user.firstName,
      last_name: user.lastName,
      email: user.email,
      job_function: user.jobFunction,
    };
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
      recaptchaRef.current.executeAsync().then((token) => {
        if (isFormInvalid) {
          setProcessing(false);
        } else {
          onSubmit(token, { user: userData, utm_url_parameters: getUtmParams(googleId) });
        }
      });
    } else {
      // For test environment
      onSubmit('test-token', { user: userData, utm_url_parameters: getUtmParams(googleId) });
    }
  };

  const renderError = inputName => errors[inputName] && (
    <div className="error-messages">{errors[inputName]}</div>
  );

  return (
    <Fragment>
      <div className={cx('user-report-form', className)}>
        {(enableTopBackButton || title) && (
          <header className="user-report-form__header">
            {enableTopBackButton && (
              <button
                type="button"
                className={cx('btn btn-outline form-back-button-mobile', { disabled: isProcessing })}
                onClick={handleGoBack}
              >
                <span className="hicon-angle-left" />
              </button>
            )}
            <h2 className="user-report-form__title">{title}</h2>
          </header>
        )}
        <form
          onSubmit={handleSubmit}
          id="user-report-form"
          className="user-report-form__form"
        >
          <div className="user-report-form__row">
            <fieldset className={cx('user-report-form__fieldset first-name', { 'has-error': Boolean(errors.firstName) })}>
              <InputText
                value={user.firstName}
                name="firstName"
                placeholder="First Name"
                onChange={handleFieldChange}
              />
              {renderError('firstName')}
            </fieldset>
            <fieldset className={cx('user-report-form__fieldset last-name', { 'has-error': Boolean(errors.lastName) })}>
              <InputText
                value={user.lastName}
                name="lastName"
                placeholder="Last Name"
                onChange={handleFieldChange}
              />
              {renderError('lastName')}
            </fieldset>
          </div>
          <fieldset className={cx('user-report-form__fieldset email', { 'has-error': Boolean(errors.email) })}>
            <InputText
              value={user.email}
              name="email"
              placeholder="Business Email"
              onChange={handleFieldChange}
            />
            {renderError('email')}
          </fieldset>
          <fieldset className={cx('user-report-form__fieldset job-title', { 'has-error': Boolean(errors.jobFunction) })}>
            <InputText
              value={user.jobFunction}
              name="jobTitle"
              placeholder="Job Title"
              onChange={value => handleFieldChange(value, { target: { name: 'jobFunction' } })}
            />
            {renderError('jobFunction')}
          </fieldset>
          {!skipAgreementField && (
            <fieldset className={cx('user-report-form__fieldset', { 'has-error': Boolean(errors.checked) })}>
              <SimpleCheckbox
                id="agreement-check"
                label={agreementText}
                checked={checked}
                gap="20px"
                onChange={handleAcceptAgreement}
              />
              {renderError('checked')}
            </fieldset>
          )}
          <div className="user-report-form__buttons">
            {enableBottomBackButton && (
              <Button
                variant="secondary"
                disabled={isProcessing}
                className="mr-xl form-back-button"
                onClick={handleGoBack}
              >
                Back
              </Button>
            )}
            <Button
              type="submit"
              loading={isProcessing}
              disabled={submitDisabled}
              loadingId="user-report-form__btn-loading"
              className="user-report-form__btn-submit"
            >
              {submitButtonText || 'Submit'}
            </Button>
          </div>
        </form>
      </div>
      {Boolean(sitekey) && (
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={sitekey}
        />
      )}
    </Fragment>
  );
};

UserReportForm.propTypes = {
  sitekey: T.string,
  title: T.string,
  isProcessing: T.bool.isRequired,
  customErrors: T.object,
  submitButtonText: T.string,
  skipAgreementField: T.bool,
  enableTopBackButton: T.bool,
  enableBottomBackButton: T.bool,
  className: T.string,

  setProcessing: T.func.isRequired,
  onStepBack: T.func,
  onSubmit: T.func.isRequired,
};

export default UserReportForm;
